import { useQuery } from '@tanstack/react-query';
import { Camera, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { MAX_CHECKIN_PHOTOS, uploadCheckoutPhoto, type CheckinQuestion } from '../lib/checkouts';
import { photoProblem, preparePhoto } from './device-history';
import { Field, Input, Select, Spinner } from './ui';

interface PendingPhoto {
  key: string;
  name: string;
  preview: string;
  id: string | null;
  error: string | null;
}

type Answer = string | boolean;

/**
 * The school's check-in form and photos of the device's condition, for one check-in. Photos upload as they're chosen, and
 * the check-in attaches them.
 */
export function useCheckinExtras(active = true) {
  const questions = useQuery({
    queryKey: ['checkin-questions'],
    queryFn: () => api.get<{ items: CheckinQuestion[] }>('/checkin-questions'),
    select: (data) => data.items,
    staleTime: 60_000,
    enabled: active,
  });
  const [values, setValues] = useState<Record<string, Answer>>({});
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const photosRef = useRef(photos);

  useEffect(() => {
    photosRef.current = photos;
  });
  useEffect(() => () => photosRef.current.forEach((photo) => URL.revokeObjectURL(photo.preview)), []);

  const list = questions.data ?? [];
  const missing = list
    .filter((question) => question.required && question.type !== 'CHECKBOX' && !String(values[question.id] ?? '').trim())
    .map((question) => question.label);
  const uploading = photos.some((photo) => !photo.id && !photo.error);

  const addPhotos = (files: File[]) => {
    for (const file of files.slice(0, Math.max(0, MAX_CHECKIN_PHOTOS - photos.length))) {
      const key = `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`;
      setPhotos((current) => [...current, { key, name: file.name, preview: URL.createObjectURL(file), id: null, error: null }]);
      void (async () => {
        try {
          const prepared = await preparePhoto(file);
          const saved = await uploadCheckoutPhoto(prepared.blob, prepared.fileName);
          setPhotos((current) => current.map((photo) => (photo.key === key ? { ...photo, id: saved.id } : photo)));
        } catch (err) {
          setPhotos((current) => current.map((photo) => (photo.key === key ? { ...photo, error: photoProblem(err) } : photo)));
        }
      })();
    }
  };

  return {
    questions: list,
    values,
    setValue: (questionId: string, value: Answer) => setValues((current) => ({ ...current, [questionId]: value })),
    photos,
    addPhotos,
    removePhoto: (key: string) =>
      setPhotos((current) =>
        current.filter((photo) => {
          if (photo.key === key) URL.revokeObjectURL(photo.preview);
          return photo.key !== key;
        }),
      ),
    reset: () => {
      setValues({});
      setPhotos((current) => {
        current.forEach((photo) => URL.revokeObjectURL(photo.preview));
        return [];
      });
    },
    missing,
    uploading,
    ready: missing.length === 0 && !uploading,
    /** Every answer, so the form's required questions are checked, and the photos that finished uploading. */
    body: () => ({ answers: values, photoIds: photos.flatMap((photo) => (photo.id ? [photo.id] : [])) }),
  };
}

export type CheckinExtras = ReturnType<typeof useCheckinExtras>;

function QuestionInput({ question, value, onChange }: { question: CheckinQuestion; value: Answer | undefined; onChange(value: Answer): void }) {
  if (question.type === 'CHECKBOX') {
    return (
      <label className="flex items-center gap-2 text-sm text-slate-800">
        <input type="checkbox" checked={value === true} onChange={(event) => onChange(event.target.checked)} className="size-4 rounded border-slate-300" />
        {question.label}
      </label>
    );
  }
  const text = typeof value === 'string' ? value : '';
  return (
    <Field label={question.label} required={question.required}>
      {question.type === 'SELECT' ? (
        <Select value={text} onChange={(event) => onChange(event.target.value)}>
          <option value="">{question.required ? 'Choose one' : 'Not answered'}</option>
          {question.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Select>
      ) : (
        <Input value={text} maxLength={1000} onChange={(event) => onChange(event.target.value)} />
      )}
    </Field>
  );
}

/** The check-in form's questions, then photos of the device's condition. */
export function CheckinExtrasFields({ extras }: { extras: CheckinExtras }) {
  return (
    <div className="space-y-4">
      {extras.questions.length > 0 && (
        <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-3.5">
          <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">Check-in form</p>
          {extras.questions.map((question) => (
            <QuestionInput key={question.id} question={question} value={extras.values[question.id]} onChange={(value) => extras.setValue(question.id, value)} />
          ))}
        </div>
      )}
      <div>
        <p className="mb-1 text-sm font-medium text-slate-700">Condition photos</p>
        <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
          {extras.photos.map((photo) => (
            <div key={photo.key} className="relative aspect-square overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
              <img src={photo.preview} alt={photo.name} className="size-full object-cover" />
              {!photo.id && !photo.error && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                  <Spinner />
                </div>
              )}
              {photo.error && (
                <div className="absolute inset-0 flex items-center justify-center bg-red-50/95 p-1 text-center text-[10px] leading-tight text-red-700">{photo.error}</div>
              )}
              <button
                type="button"
                onClick={() => extras.removePhoto(photo.key)}
                className="absolute top-1 right-1 rounded-full bg-slate-900/70 p-0.5 text-white hover:bg-slate-900"
                aria-label={`Remove ${photo.name}`}
              >
                <X className="size-3.5" />
              </button>
            </div>
          ))}
          {extras.photos.length < MAX_CHECKIN_PHOTOS && (
            <label className="flex aspect-square cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-slate-300 text-xs text-slate-500 hover:border-brand-400 hover:text-brand-700">
              <Camera className="size-5" />
              Add
              <input
                type="file"
                accept="image/*"
                multiple
                className="sr-only"
                onChange={(event) => {
                  const files = [...(event.target.files ?? [])];
                  event.target.value = '';
                  extras.addPhotos(files);
                }}
              />
            </label>
          )}
        </div>
        <p className="mt-1 text-xs text-slate-500">Optional, up to {MAX_CHECKIN_PHOTOS}. They’re resized before they upload.</p>
      </div>
    </div>
  );
}
